if (!customElements.get('component-planner')) {
  class ComponentPlanner extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.timeline = this.querySelector('[data-timeline]');
      this.inputs = {
        firstDayOfLastPeriodDate: this.querySelector(
          '[name="first_day_of_period"]'
        ),
        cycleLength: this.querySelector('[name="cycle_length"]'),
      };
      this.actionsWrapper = this.querySelector('[data-calendar-actions]');
      this.addToCalendarButtons = this.querySelectorAll(
        '[data-add-to-calendar]'
      );
      this.endDateText = this.querySelector('[data-pms-end-date]');
      this.startDateText = this.querySelector('[data-pms-start-date]');
      this.startDateInfoText = this.querySelector('[data-pms-start-date-info]');

      this.initHandlers();
      this.setCalendarDates(
        this.parseLocalDate(this.dataset.initialCalendarStartDate),
        this.parseLocalDate(this.dataset.initialCalendarEndDate)
      );
      // Re-render with the browser's locale/time zone so the timeline days
      // and dates always match the selected first day of the period.
      this.updateDates();
      this.updateTimeline();
      this.toggleCalendarActions();
    }

    initHandlers() {
      this.inputs.cycleLength.addEventListener(
        'input',
        this.handleOnCycleLengthChange.bind(this)
      );
      this.inputs.firstDayOfLastPeriodDate.addEventListener(
        'input',
        this.handleOnCycleLengthChange.bind(this)
      );
      this.addToCalendarButtons.forEach((button) =>
        button.addEventListener(
          'click',
          this.handleOnAddToCalendarButtonClick.bind(this)
        )
      );
    }

    // Calendar day (1-31) and full date for the timeline cell `offset` days
    // after the first day of the period.
    getTimelineDay(offset) {
      const firstDay = this.parseLocalDate(
        this.inputs.firstDayOfLastPeriodDate.value
      );
      if (!firstDay) return { day: offset + 1, title: '' };

      const date = new Date(firstDay);
      date.setDate(firstDay.getDate() + offset);

      return {
        day: date.getDate(),
        title: date.toLocaleString('default', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
      };
    }

    updateTimeline() {
      const cycleLength = Number(this.inputs.cycleLength.value);
      const firstPmsDay = cycleLength - 6;

      this.timeline.innerHTML = Array.from(
        Array(Number(this.inputs.cycleLength.value)).keys()
      )
        .map((index) => {
          const { day, title } = this.getTimelineDay(index);
          return `<li class="${
            index + 1 <= Number(this.dataset.periodLength) ? 'is-period' : ''
          }${
            index + 1 <= cycleLength && index + 1 >= firstPmsDay
              ? ' is-pms'
              : ''
          }" title="${title}">${day}</li>`;
        })
        .join('');
      this.timeline.innerHTML += Array.from(
        Array(Number(this.dataset.timelineOffsetDays)).keys()
      )
        .map((index) => {
          const { day, title } = this.getTimelineDay(cycleLength + index);
          return `<li class="${
            index + 1 <= Number(this.dataset.periodLength) ? 'is-period' : ''
          }${
            index + 1 <= Number(this.dataset.firstPmsDaysLength)
              ? ' is-pms'
              : ''
          }" title="${title}">${day}</li>`;
        })
        .join('');
    }

    updateDates() {
      const firstDay = this.parseLocalDate(
        this.inputs.firstDayOfLastPeriodDate.value
      );
      if (!firstDay) return;

      const cycleLength = Number(this.inputs.cycleLength.value);
      // Start PMS support 7 days before the next period:
      // first day of period + cycle length - 7 (28-day cycle => +21 days).
      const selectedDate = new Date(firstDay);
      const endDate = new Date(firstDay);

      selectedDate.setDate(firstDay.getDate() + cycleLength - 7);
      endDate.setDate(firstDay.getDate() + cycleLength + 2);

      this.endDateText.textContent = `${endDate.toLocaleString('default', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      })}`;
      this.startDateText.textContent = `${selectedDate.toLocaleString(
        'default',
        {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        }
      )}`;
      this.startDateInfoText.textContent = this.dataset.startDateInfo.replace(
        '{{ day }}',
        Number(this.inputs.cycleLength.value) - 6
      );

      this.setCalendarDates(selectedDate, endDate);
    }

    handleOnCycleLengthChange() {
      this.updateDates();
      this.updateTimeline();
      this.toggleCalendarActions();
    }

    // "YYYY-MM-DD" -> Date at local midnight. `new Date('YYYY-MM-DD')` is
    // parsed as UTC, which shifts the day back by one in US time zones.
    parseLocalDate(value) {
      const [year, month, day] = String(value || '').split('-').map(Number);
      if (!year || !month || !day) return null;
      return new Date(year, month - 1, day);
    }

    formatLocalDate(date) {
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )}`;
    }

    setCalendarDates(startDate, endDate) {
      if (!startDate || !endDate) return;
      this.calendarStartDate = this.formatLocalDate(startDate);
      this.calendarEndDate = this.formatLocalDate(endDate);
    }

    getCalendarConfig(provider) {
      const option =
        provider === 'google'
          ? 'Google'
          : provider === 'apple'
          ? 'Apple'
          : provider === 'outlook'
          ? 'Outlook.com'
          : undefined;

      if (!option)
        return new Error(
          'No provider provided for add-to-calendar-button component!'
        );

      return {
        name: this.dataset.calendarEventName,
        startDate: this.calendarStartDate,
        endDate: this.calendarEndDate,
        options: [option],
      };
    }

    toggleCalendarActions() {
      this.actionsWrapper.classList.toggle(
        'hidden',
        !this.calendarStartDate || !this.calendarEndDate
      );
    }

    handleOnAddToCalendarButtonClick(event) {
      const provider = event.target.dataset.provider;

      event.preventDefault();

      atcb_action(this.getCalendarConfig(provider));
    }
  }

  customElements.define('component-planner', ComponentPlanner);
}
